import SettingsIcon from "@mui/icons-material/Settings";
import { Stack, TableCell, TableRow, Typography } from "@mui/material";
import { EXTERNAL_SERVICE_CODE } from "types/externalService";
import { DeviceStatus } from "utils/robot/deviceStatus";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import {
  getDreameRobotErrorCodeLabel,
  isErrorDreameRobotByErrorCode,
} from "utils/robot/dreame/errorCodeMap";
import { getDreameRobotStateLabel } from "utils/robot/dreame/stateMap";
import { getDreameRobotWaterTankStatusLabel } from "utils/robot/dreame/waterTankStatusMap";

type Props = {
  deviceStatuses: DeviceStatus[];
  exCode: string;
};

export const DeviceStatusesView = ({ deviceStatuses, exCode }: Props) => {
  return (
    <>
      <TableRow>
        <TableCell colSpan={2} sx={{ bgcolor: "#f9f9f9", py: 1 }}>
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <SettingsIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            <Typography
              variant="categoryLabel"
              sx={{ color: "text.secondary" }}
            >
              デバイスステータス
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
      {exCode === EXTERNAL_SERVICE_CODE.TUYA
        ? (() => {
            const batteryRatio =
              deviceStatuses.find((s) => s.type === TuyaDeviceStatus.Battery)
                ?.status ?? "-";
            const state =
              deviceStatuses.find((s) => s.type === TuyaDeviceStatus.State)
                ?.status ?? "-";
            const isCharging = state === "charging" || state === "fullcharge";
            return (
              <TableRow>
                <TableCell>充電状況</TableCell>
                <TableCell>
                  {isCharging ? `充電中 ${batteryRatio}%` : "-"}
                </TableCell>
              </TableRow>
            );
          })()
        : deviceStatuses.map((s) => {
            const { label, value, isWarning } = getDeviceStatusField(s, exCode);
            return (
              <TableRow key={s.type}>
                <TableCell>{label}</TableCell>
                <TableCell
                  sx={{
                    color: (theme) =>
                      isWarning ? theme.palette.error.main : "inherit",
                  }}
                >
                  {isWarning && (
                    <ReportProblemIcon
                      fontSize="small"
                      color="error"
                      style={{
                        marginRight: "0.25rem",
                        verticalAlign: "middle",
                      }}
                    />
                  )}
                  {value}
                </TableCell>
              </TableRow>
            );
          })}
    </>
  );
};

const TuyaDeviceStatus = {
  State: "state",
  Battery: "batteryRatio",
} as const;

const DreameDeviceStatus = {
  State: "state",
  ErrorCode: "errorCode",
  Battery: "batteryRatio",
  WaterTankStatus: "waterTankStatus",
} as const;

type ViewContent = {
  label: string;
  value: string;
  isWarning?: boolean;
};

const getDeviceStatusField = (
  deviceStatus: DeviceStatus,
  exCode: string
): ViewContent => {
  if (exCode === EXTERNAL_SERVICE_CODE.TUYA) {
    switch (deviceStatus.type) {
      case TuyaDeviceStatus.Battery:
        return {
          label: "充電状況",
          value: `${deviceStatus.status} %`,
        };
    }
  } else if (exCode === EXTERNAL_SERVICE_CODE.DREAME) {
    switch (deviceStatus.type) {
      case DreameDeviceStatus.State:
        return {
          label: "ステータス",
          value: getDreameRobotStateLabel(deviceStatus.status),
        };
      case DreameDeviceStatus.ErrorCode:
        return {
          label: "エラー",
          value: getDreameRobotErrorCodeLabel(deviceStatus.status),
          isWarning: isErrorDreameRobotByErrorCode(deviceStatus.status),
        };
      case DreameDeviceStatus.Battery:
        return {
          label: "充電状況",
          value: `${deviceStatus.status} %`,
        };
      case DreameDeviceStatus.WaterTankStatus:
        return {
          label: "水タンク状態",
          value: getDreameRobotWaterTankStatusLabel(deviceStatus.status),
        };
    }
  }
  return {
    label: "不明なデータ",
    value: "unknown",
  };
};
